import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import { Context } from "../../../../index";
import MachinePlaceService, {
  IMachinePlace,
} from "../../../../services/MachinePlaceService";
import { notify } from "../../../../services/notify";
import ButtonComponent from "../../../../components/buttons/ButtonComponent";

/**
 * `РГ-07` (ФЗ-152): вкладка «Машиноместа» карточки объекта.
 *
 * Список мест объекта и форма заведения. Заводит только `root` — до первой
 * регистрации владельцев (закрытая модель); список тоже показывается только ему,
 * потому что маршрут объявлен только для `root`.
 *
 * Обозначение уходит на сервер дословно. Столкновение приведённых обозначений
 * (`РГ-11`) сервер называет причиной в ответе `409` — форма показывает её как
 * есть, а не подменяет общей фразой.
 */

function reasonOf(error: any): string {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Не удалось выполнить запрос"
  );
}

const MachinePlaces: FC<{ plantId: number }> = observer(({ plantId }) => {
  const { store } = useContext(Context);
  const [places, setPlaces] = useState<IMachinePlace[]>([]);
  const [number, setNumber] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setSaving] = useState(false);

  const isRoot = store.user?.role === "root";

  const load = useCallback(async () => {
    try {
      const { data } = await MachinePlaceService.list(plantId);
      setPlaces(data);
    } catch (error) {
      notify.error(reasonOf(error));
    }
  }, [plantId]);

  useEffect(() => {
    if (plantId) load();
  }, [plantId, load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!number.trim()) {
      notify.error("Нужно обозначение машиноместа");
      return;
    }

    setSaving(true);

    try {
      const { data } = await MachinePlaceService.create({
        plantId,
        number,
        description: description || undefined,
      });

      setPlaces((prev) => [...prev, data]);
      setNumber("");
      setDescription("");
      notify.success("Машиноместо заведено");
    } catch (error) {
      notify.error(reasonOf(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="machinePlaces">
      <div className="machinePlaces_title">МАШИНОМЕСТА</div>

      <table className="machinePlaces_table">
        <thead>
          <tr>
            <th>Обозначение</th>
            <th>Описание</th>
          </tr>
        </thead>
        <tbody>
          {places.length === 0 ? (
            <tr>
              <td colSpan={2}>места не заведены</td>
            </tr>
          ) : (
            places.map((place) => (
              <tr key={place.id}>
                <td>{place.number || "—"}</td>
                <td>{place.description || ""}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {isRoot ? (
        <form className="machinePlaces_form" onSubmit={create}>
          <input
            className="machinePlaces_input"
            aria-label="обозначение машиноместа"
            placeholder="Обозначение, как в договоре"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
          <input
            className="machinePlaces_input"
            aria-label="описание машиноместа"
            placeholder="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <ButtonComponent title="Добавить место" disabled={isSaving} />
        </form>
      ) : null}
    </div>
  );
});

export default MachinePlaces;
